import React, { useEffect, useState, useRef } from 'react';
import { useParams, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  TextField,
  Alert,
  CircularProgress,
  Paper,
  Grid,
  FormControl,
  FormControlLabel,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  LinearProgress,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import KeyboardArrowLeft from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight';
import { getPublicForm, submitPublicApplication, uploadPublicApplicationDocuments, type FormSection, type PublicApplicationPayload } from '../services/rentalApplicationService';

/** 100 Points of ID – document types with point values (property industry standard). */
const DOCUMENT_TYPE_OPTIONS = [
  { value: 'australian_passport_70', label: 'Australian Passport (current) – 70 points' },
  { value: 'foreign_passport_70', label: 'Foreign Passport (current) – 70 points' },
  { value: 'australian_driver_licence_40', label: 'Australian Driver Licence – 40 points' },
  { value: 'proof_of_age_40', label: 'Australian Proof of Age Card – 40 points' },
  { value: 'birth_certificate_70', label: 'Birth Certificate – 70 points' },
  { value: 'citizenship_certificate_70', label: 'Citizenship Certificate – 70 points' },
  { value: 'medicare_card_25', label: 'Medicare Card – 25 points' },
  { value: 'bank_card_25', label: 'Bank Card (with name) – 25 points' },
  { value: 'utility_bill_25', label: 'Utility Bill (recent) – 25 points' },
  { value: 'rates_notice_25', label: 'Rates Notice – 25 points' },
  { value: 'centrelink_card_25', label: 'Centrelink Card – 25 points' },
  { value: 'student_id_20', label: 'Student ID – 20 points' },
  { value: 'payslip_1', label: 'Payslip 1 (income proof)' },
  { value: 'payslip_2', label: 'Payslip 2 (income proof)' },
  { value: 'income_proof', label: 'Other income proof' },
  { value: 'bank_statement', label: 'Bank Statement' },
  { value: 'rental_ledger', label: 'Rental Ledger' },
  { value: 'other', label: 'Other' },
];

/** Points for 100-point ID check (non-ID types are 0). */
const DOCUMENT_POINTS: Record<string, number> = {
  australian_passport_70: 70,
  foreign_passport_70: 70,
  australian_driver_licence_40: 40,
  proof_of_age_40: 40,
  birth_certificate_70: 70,
  citizenship_certificate_70: 70,
  medicare_card_25: 25,
  bank_card_25: 25,
  utility_bill_25: 25,
  rates_notice_25: 25,
  centrelink_card_25: 25,
  student_id_20: 20,
};

const DRAFT_STORAGE_KEY_PREFIX = 'rental-application-draft-';
const DRAFT_SAVE_DEBOUNCE_MS = 800;

function getDraftKey(token: string): string {
  return `${DRAFT_STORAGE_KEY_PREFIX}${token}`;
}

function loadDraft(token: string): { form: Record<string, string | number | boolean>; repeatableCounts: Record<string, number>; activeStep: number } | null {
  try {
    const raw = localStorage.getItem(getDraftKey(token));
    if (!raw) return null;
    const data = JSON.parse(raw) as { form?: Record<string, string | number | boolean>; repeatableCounts?: Record<string, number>; activeStep?: number };
    if (!data || typeof data !== 'object') return null;
    return {
      form: data.form && typeof data.form === 'object' ? data.form : {},
      repeatableCounts: data.repeatableCounts && typeof data.repeatableCounts === 'object' ? data.repeatableCounts : {},
      activeStep: typeof data.activeStep === 'number' && data.activeStep >= 0 ? data.activeStep : 0,
    };
  } catch {
    return null;
  }
}

function saveDraft(token: string, form: Record<string, string | number | boolean>, repeatableCounts: Record<string, number>, activeStep: number): void {
  try {
    localStorage.setItem(getDraftKey(token), JSON.stringify({ form, repeatableCounts, activeStep }));
  } catch {
    // quota or disabled localStorage
  }
}

function clearDraft(token: string): void {
  try {
    localStorage.removeItem(getDraftKey(token));
  } catch {}
}

/** Format ISO date string (YYYY-MM-DD) for display (e.g. "23 Feb 2026"). */
function formatDisplayDate(isoDate: string): string {
  try {
    const d = new Date(isoDate + 'T12:00:00');
    if (Number.isNaN(d.getTime())) return isoDate;
    return d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
  } catch {
    return isoDate;
  }
}

/** Today's date in local timezone as YYYY-MM-DD (for declaration date so it matches computer time). */
function getTodayLocalDateString(): string {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

const RentalApply: React.FC = () => {
  const { agencySlug, token } = useParams<{ agencySlug: string; token: string }>();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [formConfig, setFormConfig] = useState<Awaited<ReturnType<typeof getPublicForm>> | null>(null);
  const [form, setForm] = useState<Record<string, string | number | boolean>>({});
  const [supportingDocuments, setSupportingDocuments] = useState<Array<{ file: File; documentType: string; applicantRef: string }>>([]);
  const [documentTypeForNewFiles, setDocumentTypeForNewFiles] = useState(DOCUMENT_TYPE_OPTIONS[0]?.value ?? 'other');
  /** Document type for "new file" upload per applicant (Supporting Documents section). */
  const [documentTypeForNewFilesByApplicant, setDocumentTypeForNewFilesByApplicant] = useState<Record<string, string>>({});
  const [activeStep, setActiveStep] = useState(0);
  /** After submit, token for applicant to view application later (optional) */
  const [submittedApplicantToken, setSubmittedApplicantToken] = useState<string | null>(null);
  /** After submit, 6-digit access code for viewing application (optional) */
  const [submittedApplicantCode, setSubmittedApplicantCode] = useState<string | null>(null);
  /** Number of blocks for each repeatable section (e.g. { '2b': 1, '2c': 0 }) */
  const [repeatableCounts, setRepeatableCounts] = useState<Record<string, number>>({});
  const [draftRestored, setDraftRestored] = useState(false);
  const [draftSavedAt, setDraftSavedAt] = useState<number | null>(null);
  const saveDraftTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!token || !agencySlug) {
      setError('Invalid application link');
      setLoading(false);
      return;
    }
    getPublicForm(agencySlug, token)
      .then((config) => {
        console.log('[RentalApply] getPublicForm response:', {
          summary: config.summary,
          summaryWeeklyRent: config.summary?.weeklyRent,
          summaryBondAmount: config.summary?.bondAmount,
          hasSections: !!config.sections?.length,
          formSupported: config.formSupported,
        });
        setFormConfig(config);
        if (config.sections?.length) {
          const initial: Record<string, string | number | boolean> = {};
          const counts: Record<string, number> = {};
          if (config.summary) {
            for (const section of config.sections) {
              if (section.repeatable && section.id) {
                counts[section.id] = 0;
              }
              for (const field of section.fields) {
                if (field.name === 'propertyAddress' && config.summary!.address) initial[field.name] = config.summary!.address;
                else if (field.name === 'suburb' && config.summary!.suburb) initial[field.name] = config.summary!.suburb;
                else if (field.name === 'postcode' && config.summary!.postcode) initial[field.name] = config.summary!.postcode;
                else if ((field.name === 'weeklyRent' || field.name === 'advertisedWeeklyRent') && config.summary!.weeklyRent != null) initial[field.name] = config.summary!.weeklyRent;
                else if (field.name === 'bondAmount' && config.summary!.bondAmount != null) initial[field.name] = config.summary!.bondAmount;
                else if (field.name === 'declarationDate') initial[field.name] = getTodayLocalDateString();
              }
            }
          }
          if (Object.keys(counts).length) setRepeatableCounts((prev) => ({ ...prev, ...counts }));
          console.log('[RentalApply] initial prefill from summary:', initial);
          setForm((prev) => ({ ...initial, ...prev }));

          const draft = token ? loadDraft(token) : null;
          if (draft && (Object.keys(draft.form).length > 0 || Object.keys(draft.repeatableCounts).length > 0)) {
            setForm((prev) => ({ ...prev, ...draft.form }));
            setRepeatableCounts((prev) => ({ ...prev, ...draft.repeatableCounts }));
            setActiveStep(Math.min(draft.activeStep, (config.sections?.length ?? 1) - 1));
            setDraftRestored(true);
          }
        }
      })
      .catch((e) => {
        setError(e?.message || 'Failed to load form');
      })
      .finally(() => setLoading(false));
  }, [token]);

  // Save draft to localStorage (debounced) so applicant can continue later
  useEffect(() => {
    if (!token || !formConfig?.sections?.length) return;
    if (saveDraftTimerRef.current) clearTimeout(saveDraftTimerRef.current);
    saveDraftTimerRef.current = setTimeout(() => {
      saveDraftTimerRef.current = null;
      saveDraft(token, form, repeatableCounts, activeStep);
      setDraftSavedAt(Date.now());
    }, DRAFT_SAVE_DEBOUNCE_MS);
    return () => {
      if (saveDraftTimerRef.current) clearTimeout(saveDraftTimerRef.current);
    };
  }, [token, form, repeatableCounts, activeStep, formConfig?.sections?.length]);

  // Hide "Draft saved" message after 2.5s
  useEffect(() => {
    if (draftSavedAt == null) return;
    const t = setTimeout(() => setDraftSavedAt(null), 2500);
    return () => clearTimeout(t);
  }, [draftSavedAt]);

  // Apply summary-based prefill when formConfig (and summary) becomes available, so rent/bond always show when API returns them
  useEffect(() => {
    if (!formConfig?.summary || !formConfig.sections?.length) return;
    const s = formConfig.summary;
    console.log('[RentalApply] prefill effect: summary.weeklyRent=', s.weeklyRent, 'summary.bondAmount=', s.bondAmount);
    setForm((prev) => {
      const next = { ...prev };
      if (s.weeklyRent != null) {
        next.weeklyRent = s.weeklyRent;
        next.advertisedWeeklyRent = s.weeklyRent;
      }
      if (s.bondAmount != null) next.bondAmount = s.bondAmount;
      console.log('[RentalApply] prefill effect: next form state (rent/bond):', { weeklyRent: next.weeklyRent, bondAmount: next.bondAmount });
      return next;
    });
  }, [formConfig?.summary?.weeklyRent, formConfig?.summary?.bondAmount]);

  // When the declaration step is shown, set declaration date to today (local time) so it matches computer date
  useEffect(() => {
    if (!formConfig?.sections?.length || activeStep >= formConfig.sections.length) return;
    const section = formConfig.sections[activeStep];
    const hasDeclarationDate = section?.fields?.some((f) => f.name === 'declarationDate');
    if (!hasDeclarationDate) return;
    const today = getTodayLocalDateString();
    setForm((prev) => {
      if (prev.declarationDate === today) return prev;
      return { ...prev, declarationDate: today };
    });
  }, [formConfig?.sections, activeStep]);

  const handleChange = (name: string, value: string | number | boolean) => {
    setForm((prev) => {
      const next = { ...prev, [name]: value };
      if (name === 'primaryApplicant_idType') {
        if (value === 'Driver Licence') next.primaryApplicant_idCountry = '';
        else if (value === 'Passport') next.primaryApplicant_idState = '';
      }
      return next;
    });
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>, applicantRef: string) => {
    const files = e.target.files;
    if (!files?.length) return;
    const maxFiles = 10;
    const maxSizePerFile = 10 * 1024 * 1024; // 10MB
    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    const docType = documentTypeForNewFilesByApplicant[applicantRef] ?? documentTypeForNewFiles;
    const toAdd: Array<{ file: File; documentType: string; applicantRef: string }> = [];
    for (let i = 0; i < files.length && supportingDocuments.length + toAdd.length < maxFiles; i++) {
      const f = files[i];
      if (!allowedTypes.includes(f.type)) continue;
      if (f.size > maxSizePerFile) continue;
      toAdd.push({ file: f, documentType: docType, applicantRef });
    }
    if (toAdd.length) setSupportingDocuments((prev) => [...prev, ...toAdd].slice(0, maxFiles));
    e.target.value = '';
  };

  const removeDocument = (index: number) => {
    setSupportingDocuments((prev) => prev.filter((_, i) => i !== index));
  };

  // Document type is fixed at the time of upload; do not allow changing it afterwards.

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token || !formConfig || !agencySlug) return;
    setError(null);
    if (formConfig.sections?.length) {
      const currentSection = formConfig.sections[activeStep];
      if (currentSection?.fields?.length) {
        const missing: string[] = [];
        for (const field of currentSection.fields) {
          if (!field.required) continue;
          const value = form[field.name];
          if (value === undefined || value === null) { missing.push(field.label); continue; }
          if (field.type === 'checkbox' && value !== true) { missing.push(field.label); continue; }
          if (field.type === 'number' && value === '') { missing.push(field.label); continue; }
          if (typeof value === 'string' && value.trim() === '') missing.push(field.label);
        }
        if (missing.length > 0) {
          setError(`Please fill in all required fields: ${missing.join(', ')}`);
          return;
        }
      }
    }
    const coCount = repeatableCounts['2b'] ?? 0;
    const filledCoIndices = Array.from({ length: coCount }, (_, i) => i).filter((i) => {
      const first = String(form[`coApplicant_${i}_firstName`] ?? '').trim();
      const last = String(form[`coApplicant_${i}_lastName`] ?? '').trim();
      return first !== '' || last !== '';
    });
    const applicantRefs: Array<{ ref: string; label: string }> = [
      { ref: 'primary', label: 'Primary applicant' },
      ...filledCoIndices.map((i) => ({ ref: `coApplicant_${i}`, label: `Co-applicant ${i + 1}` })),
    ];
    for (const { ref, label } of applicantRefs) {
      const docs = supportingDocuments.filter((item) => (item.applicantRef || 'primary') === ref);
      if (docs.length === 0) {
        setError(`Supporting documents are required for ${label}. Each applicant must upload documents and provide at least 100 points of ID.`);
        return;
      }
      const points = docs.reduce((sum, item) => sum + (DOCUMENT_POINTS[item.documentType] ?? 0), 0);
      if (points < 100) {
        setError(`${label} has ${points} points of ID. Each applicant must provide at least 100 points of ID (e.g. passport 70 + driver licence 40).`);
        return;
      }
    }
    setSubmitting(true);
    try {
      const hasPrimaryPrefix = Object.keys(form).some((k) => k.startsWith('primaryApplicant_'));
      let payload: PublicApplicationPayload | Record<string, unknown>;

      if (hasPrimaryPrefix) {
        const primaryApplicant: Record<string, unknown> = {};
        const coApplicants: Array<Record<string, unknown>> = [];
        const childOccupants: Array<{ fullName: string; dateOfBirth?: string }> = [];

        for (const [key, value] of Object.entries(form)) {
          if (value === undefined || value === '') continue;
          if (key.startsWith('primaryApplicant_')) {
            primaryApplicant[key.replace(/^primaryApplicant_/, '')] = value;
          } else if (key.startsWith('coApplicant_')) {
            const m = key.match(/^coApplicant_(\d+)_(.+)$/);
            if (m) {
              const i = parseInt(m[1], 10);
              const field = m[2];
              if (!coApplicants[i]) coApplicants[i] = {};
              (coApplicants[i] as Record<string, unknown>)[field] = value;
            }
          } else if (key.startsWith('child_')) {
            const m = key.match(/^child_(\d+)_(.+)$/);
            if (m) {
              const i = parseInt(m[1], 10);
              const field = m[2];
              if (!childOccupants[i]) childOccupants[i] = { fullName: '' };
              if (field === 'fullName') childOccupants[i].fullName = String(value);
              else if (field === 'dateOfBirth') childOccupants[i].dateOfBirth = String(value);
            }
          } else {
            primaryApplicant[key] = value;
          }
        }
        const coFiltered = coApplicants.filter((c) => c && (String(c.firstName || '').trim() || String(c.lastName || '').trim()));
        const childFiltered = childOccupants.filter((c) => c && String(c.fullName || '').trim());
        payload = { primaryApplicant, coApplicants: coFiltered, childOccupants: childFiltered };
      } else {
        payload = { ...form } as Record<string, unknown>;
      }
      const result = await submitPublicApplication(agencySlug, token, payload);
      const applicationId = result.applicationId;
      if (result.applicantAccessToken) setSubmittedApplicantToken(result.applicantAccessToken);
      if (result.applicantAccessCode) setSubmittedApplicantCode(result.applicantAccessCode);
      if (supportingDocuments.length > 0 && agencySlug) {
        const docsForUpload = supportingDocuments.map(({ file, documentType, applicantRef }) => ({
          file,
          documentType: DOCUMENT_TYPE_OPTIONS.find((o) => o.value === documentType)?.label ?? documentType,
          applicantRef: applicantRef || 'primary',
        }));
        await uploadPublicApplicationDocuments(agencySlug, token, applicationId, docsForUpload);
      }
      clearDraft(token);
      setSuccess(true);
    } catch (e: any) {
      setError(e?.message || 'Failed to submit');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error && !formConfig) {
    return (
      <Container maxWidth="sm" sx={{ py: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  if (success) {
    return (
      <Container maxWidth="sm" sx={{ py: 4 }}>
        {formConfig?.banner?.bannerImageData && (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', width: '100%', mb: 2 }}>
            <img
              src={`data:${formConfig.banner.bannerImageType || 'image/png'};base64,${formConfig.banner.bannerImageData}`}
              alt="Agency"
              style={{ maxWidth: '100%', maxHeight: 80, objectFit: 'contain' }}
            />
            {formConfig.banner.bannerTitle && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>{formConfig.banner.bannerTitle}</Typography>
            )}
          </Box>
        )}
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="h5" gutterBottom color="primary">
            Application submitted
          </Typography>
          <Typography color="text.secondary">
            Thank you. The agent will review your application and be in touch.
          </Typography>
          {submittedApplicantToken && (
            <Box sx={{ mt: 3 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Save this link and your access code to view your application status later:
              </Typography>
              {submittedApplicantCode && (
                <Paper variant="outlined" sx={{ p: 1.5, mb: 1.5, textAlign: 'center', bgcolor: 'action.hover' }}>
                  <Typography variant="caption" color="text.secondary">Access code</Typography>
                  <Typography variant="h6" fontWeight="700" letterSpacing={2}>{submittedApplicantCode}</Typography>
                </Paper>
              )}
              <Button
                component={RouterLink}
                to={`/rental-apply/view/${submittedApplicantToken}`}
                variant="outlined"
                fullWidth
                sx={{ textAlign: 'center' }}
              >
                View your application
              </Button>
            </Box>
          )}
        </Paper>
      </Container>
    );
  }

  if (!formConfig) return null;

  if (formConfig.formSupported === false) {
    return (
      <Container maxWidth="sm" sx={{ py: 4 }}>
        {formConfig.banner?.bannerImageData && (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', width: '100%', mb: 2 }}>
            <img
              src={`data:${formConfig.banner.bannerImageType || 'image/png'};base64,${formConfig.banner.bannerImageData}`}
              alt="Agency"
              style={{ maxWidth: '100%', maxHeight: 80, objectFit: 'contain' }}
            />
            {formConfig.banner.bannerTitle && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>{formConfig.banner.bannerTitle}</Typography>
            )}
          </Box>
        )}
        <Typography variant="h5" gutterBottom fontWeight="bold">
          Rental application
        </Typography>
        {formConfig.summary?.address && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {formConfig.summary.address}
            {formConfig.summary.dateAvailable && ` • Available from ${formatDisplayDate(formConfig.summary.dateAvailable)}`}
          </Typography>
        )}
        <Alert severity="info" sx={{ mt: 2 }}>
          {formConfig.unsupportedMessage ?? 'Online application form is not available for this property. Please contact the agent for a paper form or alternative.'}
        </Alert>
      </Container>
    );
  }

  const { summary, sections, formFields } = formConfig;
  const useSections = Boolean(sections?.length);

  console.log('[RentalApply] render: form.weeklyRent=', form.weeklyRent, 'form.bondAmount=', form.bondAmount, 'summary.weeklyRent=', summary?.weeklyRent, 'summary.bondAmount=', summary?.bondAmount);

  const renderField = (field: (typeof formFields)[0]) => {
    const isReadOnlyFromProperty =
      (field.name === 'weeklyRent' && formConfig.summary?.weeklyRent != null) ||
      (field.name === 'bondAmount' && formConfig.summary?.bondAmount != null) ||
      (field.name === 'advertisedWeeklyRent' && formConfig.summary?.weeklyRent != null) ||
      (field.name === 'propertyAddress' && (formConfig.summary?.address != null && formConfig.summary?.address !== '')) ||
      (field.name === 'suburb' && (formConfig.summary?.suburb != null && formConfig.summary?.suburb !== '')) ||
      (field.name === 'postcode' && (formConfig.summary?.postcode != null && formConfig.summary?.postcode !== ''));

    if (field.type === 'checkbox') {
      return (
        <FormControlLabel
          control={
            <Checkbox
              checked={Boolean(form[field.name])}
              onChange={(e) => handleChange(field.name, e.target.checked)}
              required={field.required}
            />
          }
          label={field.label}
        />
      );
    }
    if (field.type === 'select') {
      const isIdStateOrCountry = field.name === 'primaryApplicant_idState' || field.name === 'primaryApplicant_idCountry';
      return (
        <FormControl fullWidth required={field.required || isIdStateOrCountry}>
          <InputLabel>{field.label}</InputLabel>
          <Select
            value={form[field.name] ?? ''}
            label={field.label}
            onChange={(e) => handleChange(field.name, e.target.value)}
          >
            {isIdStateOrCountry && <MenuItem value="">Select...</MenuItem>}
            {(field.options || []).map((opt) => (
              <MenuItem key={opt} value={opt}>{opt}</MenuItem>
            ))}
          </Select>
        </FormControl>
      );
    }
    if (field.type === 'textarea') {
      return (
        <TextField
          fullWidth
          label={field.label}
          placeholder={field.placeholder}
          required={field.required}
          multiline
          rows={3}
          value={form[field.name] ?? ''}
          onChange={(e) => handleChange(field.name, e.target.value)}
        />
      );
    }
    if (field.type === 'date') {
      const raw = form[field.name];
      const dateValue =
        typeof raw === 'string' && raw
          ? (raw.includes('T') ? raw.split('T')[0] : raw)
          : '';
      return (
        <TextField
          fullWidth
          label={field.label}
          type="date"
          required={field.required}
          value={dateValue}
          onChange={(e) => handleChange(field.name, e.target.value)}
          InputLabelProps={{ shrink: true }}
        />
      );
    }
    return (
      <TextField
        fullWidth
        label={field.label}
        placeholder={field.placeholder}
        type={field.type === 'number' ? 'number' : field.type === 'email' ? 'email' : field.type === 'tel' ? 'tel' : 'text'}
        required={field.required}
        value={form[field.name] ?? ''}
        onChange={(e) =>
          handleChange(
            field.name,
            field.type === 'number' ? (e.target.value === '' ? '' : Number(e.target.value)) : e.target.value
          )
        }
        inputProps={Object.assign(
          {},
          isReadOnlyFromProperty ? { readOnly: true } : {},
          field.type === 'number' ? { min: 0, step: 1 } : {}
        )}
        helperText={isReadOnlyFromProperty ? 'From property listing (cannot be changed)' : undefined}
      />
    );
  };

  const isSupportingDocumentsSection = (title: string) =>
    /supporting\s+documents/i.test(title);

  /** Show Licence State only when ID type is Driver Licence; show Passport Country only when ID type is Passport. */
  const shouldShowIdField = (fieldName: string): boolean => {
    if (fieldName === 'primaryApplicant_idState') return form.primaryApplicant_idType === 'Driver Licence';
    if (fieldName === 'primaryApplicant_idCountry') return form.primaryApplicant_idType === 'Passport';
    return true;
  };

  /** In rental history section, show detail fields only when applicant has prior rental history. */
  const shouldShowRentalHistoryField = (field: { name: string }, section: FormSection): boolean => {
    const hasGate = section.fields.some((f) => f.name === 'priorRentalHistory');
    if (!hasGate) return true;
    if (field.name === 'priorRentalHistory') return true;
    return form.priorRentalHistory === 'Yes';
  };

  /** Short step label from section title (e.g. "SECTION 1 – PROPERTY DETAILS" → "Property details") */
  const getStepLabel = (section: FormSection) => {
    const match = section.title.match(/SECTION\s+\d+\s*[–-]\s*(.+)/i);
    return match ? match[1].trim() : section.title;
  };

  const renderOneSection = (section: FormSection) => (
    <Box key={section.id}>
      <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
        {section.title}
      </Typography>
      {section.subtitle && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {section.subtitle}
        </Typography>
      )}
      {isSupportingDocumentsSection(section.title) && (() => {
        const coCount = repeatableCounts['2b'] ?? 0;
        const applicantRefs: Array<{ ref: string; label: string }> = [
          { ref: 'primary', label: 'Primary Applicant' },
          ...Array.from({ length: coCount }, (_, i) => ({ ref: `coApplicant_${i}`, label: `Co-Applicant (Adult) ${i + 1}` })),
        ];
        return (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" fontWeight="600" sx={{ mt: 1, mb: 0.5 }}>
              How 100 Points of ID Works
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Each applicant (primary and co-applicants) must provide documents totaling at least 100 points. Upload documents for each person below.
            </Typography>
            <TableContainer component={Paper} variant="outlined" sx={{ mb: 2, maxWidth: 500 }}>
              <Table size="small" aria-label="100 Points ID table">
                <TableHead>
                  <TableRow>
                    <TableCell><strong>Document</strong></TableCell>
                    <TableCell align="right"><strong>Points</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow><TableCell>Australian Passport (current)</TableCell><TableCell align="right">70</TableCell></TableRow>
                  <TableRow><TableCell>Foreign Passport (current)</TableCell><TableCell align="right">70</TableCell></TableRow>
                  <TableRow><TableCell>Australian Driver Licence</TableCell><TableCell align="right">40</TableCell></TableRow>
                  <TableRow><TableCell>Australian Proof of Age Card</TableCell><TableCell align="right">40</TableCell></TableRow>
                  <TableRow><TableCell>Birth Certificate</TableCell><TableCell align="right">70</TableCell></TableRow>
                  <TableRow><TableCell>Citizenship Certificate</TableCell><TableCell align="right">70</TableCell></TableRow>
                  <TableRow><TableCell>Medicare Card</TableCell><TableCell align="right">25</TableCell></TableRow>
                  <TableRow><TableCell>Bank Card (with name)</TableCell><TableCell align="right">25</TableCell></TableRow>
                  <TableRow><TableCell>Utility Bill (recent)</TableCell><TableCell align="right">25</TableCell></TableRow>
                  <TableRow><TableCell>Rates Notice</TableCell><TableCell align="right">25</TableCell></TableRow>
                  <TableRow><TableCell>Centrelink Card</TableCell><TableCell align="right">25</TableCell></TableRow>
                  <TableRow><TableCell>Student ID</TableCell><TableCell align="right">20</TableCell></TableRow>
                </TableBody>
              </Table>
            </TableContainer>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Upload PDF or images per applicant. Max 10 files total, 10MB each.
            </Typography>
            {applicantRefs.map(({ ref: applicantRef, label }) => (
              <Paper key={applicantRef} variant="outlined" sx={{ p: 2, mb: 2 }}>
                <Typography variant="subtitle2" fontWeight="600" sx={{ mb: 1.5 }}>{label} – Supporting documents</Typography>
                <FormControl fullWidth size="small" sx={{ mb: 1.5, maxWidth: 400 }}>
                  <InputLabel id={`doc-type-new-${applicantRef}`}>Document type for upload</InputLabel>
                  <Select
                    labelId={`doc-type-new-${applicantRef}`}
                    value={documentTypeForNewFilesByApplicant[applicantRef] ?? documentTypeForNewFiles}
                    label="Document type for upload"
                    onChange={(e) => setDocumentTypeForNewFilesByApplicant((prev) => ({ ...prev, [applicantRef]: e.target.value }))}
                  >
                    {DOCUMENT_TYPE_OPTIONS.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Button
                  component="label"
                  variant="outlined"
                  size="small"
                  startIcon={<CloudUploadIcon />}
                  sx={{ mb: 1, display: 'block' }}
                >
                  Choose files
                  <input
                    type="file"
                    hidden
                    multiple
                    accept=".pdf,application/pdf,image/jpeg,image/jpg,image/png,image/webp"
                    onChange={(e) => handleFileSelect(e, applicantRef)}
                  />
                </Button>
                <List dense sx={{ bgcolor: 'action.hover', borderRadius: 1 }}>
                  {supportingDocuments.map((item, index) => (item.applicantRef || 'primary') !== applicantRef ? null : (
                    <ListItem
                      key={index}
                      sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}
                    >
                      <ListItemText
                        primary={item.file.name}
                        secondary={`${(item.file.size / 1024).toFixed(1)} KB`}
                        sx={{ flex: '1 1 160px', minWidth: 0 }}
                      />
                      <Box sx={{ flex: '1 1 220px', minWidth: 220 }}>
                        <Typography variant="caption" color="text.secondary">
                          Document type
                        </Typography>
                        <Typography variant="body2">
                          {DOCUMENT_TYPE_OPTIONS.find((opt) => opt.value === item.documentType)?.label ?? 'Unknown type'}
                        </Typography>
                      </Box>
                      <IconButton
                        edge="end"
                        size="small"
                        onClick={() => removeDocument(index)}
                        aria-label="Remove"
                        sx={{ ml: 'auto' }}
                      >
                        <DeleteOutlineIcon />
                      </IconButton>
                    </ListItem>
                  ))}
                </List>
              </Paper>
            ))}
          </Box>
        );
      })()}
      {section.repeatable && section.blockPrefix != null && section.maxBlocks != null ? (
        <Box>
          {Array.from({ length: repeatableCounts[section.id] ?? 0 }, (_, i) => (
            <Paper key={i} variant="outlined" sx={{ p: 2, mb: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                <Typography variant="subtitle2">
                  {section.blockPrefix === 'coApplicant' ? `Co-Applicant (Adult) ${i + 1}` : `Child ${i + 1}`}
                </Typography>
                {(repeatableCounts[section.id] ?? 0) > 0 && (
                  <IconButton
                    size="small"
                    onClick={() =>
                      setRepeatableCounts((prev) => ({
                        ...prev,
                        [section.id]: Math.max(0, (prev[section.id] ?? 0) - 1),
                      }))
                    }
                    aria-label="Remove"
                  >
                    <DeleteOutlineIcon />
                  </IconButton>
                )}
              </Box>
              <Grid container spacing={2}>
                {section.fields.map((field) => {
                  const indexedField = { ...field, name: `${section.blockPrefix}_${i}_${field.name}` };
                  return (
                    <Grid item xs={12} sm={section.blockPrefix === 'child' ? 6 : 6} key={indexedField.name}>
                      {renderField(indexedField)}
                    </Grid>
                  );
                })}
              </Grid>
            </Paper>
          ))}
          {(repeatableCounts[section.id] ?? 0) < section.maxBlocks && (
            <Button
              startIcon={<AddIcon />}
              variant="outlined"
              size="small"
              onClick={() =>
                setRepeatableCounts((prev) => ({
                  ...prev,
                  [section.id]: (prev[section.id] ?? 0) + 1,
                }))
              }
            >
              {section.blockPrefix === 'coApplicant' ? 'Add Co-Applicant (Adult)' : 'Add Child'}
            </Button>
          )}
        </Box>
      ) : (
        <Grid container spacing={2}>
          {section.fields.filter((field) => shouldShowIdField(field.name) && shouldShowRentalHistoryField(field, section)).map((field) => (
            <Grid item xs={12} key={field.name}>
              {renderField(field)}
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  );

  const totalSteps = useSections ? sections!.length : 1;
  const isLastStep = useSections && activeStep === totalSteps - 1;

  const isFieldFilled = (field: { name: string; type: string; required: boolean }, value: unknown): boolean => {
    if (value === undefined || value === null) return false;
    if (field.type === 'checkbox') return value === true;
    if (field.type === 'number') return value !== '' && Number(value) >= 0;
    if (typeof value === 'string') return value.trim() !== '';
    return true;
  };

  const getCurrentStepMissingRequired = (): string[] => {
    if (!useSections || !sections?.length) return [];
    const section = sections[activeStep];
    if (!section?.fields?.length) return [];
    const missing: string[] = [];
    if (section.repeatable && section.blockPrefix != null) {
      const count = repeatableCounts[section.id] ?? 0;
      for (let i = 0; i < count; i++) {
        for (const field of section.fields) {
          if (!field.required) continue;
          const name = `${section.blockPrefix}_${i}_${field.name}`;
          const value = form[name];
          if (!isFieldFilled(field, value)) missing.push(`${field.label} (${section.blockPrefix === 'coApplicant' ? `Co-Applicant ${i + 1}` : `Child ${i + 1}`})`);
        }
      }
    } else {
      for (const field of section.fields) {
        if (!shouldShowIdField(field.name)) continue;
        if (!field.required) {
          if (field.name === 'primaryApplicant_idState' && form.primaryApplicant_idType === 'Driver Licence' && !isFieldFilled(field, form.primaryApplicant_idState)) missing.push(field.label);
          else if (field.name === 'primaryApplicant_idCountry' && form.primaryApplicant_idType === 'Passport' && !isFieldFilled(field, form.primaryApplicant_idCountry)) missing.push(field.label);
          continue;
        }
        const value = form[field.name];
        if (!isFieldFilled(field, value)) missing.push(field.label);
      }
    }
    return missing;
  };

  const handleNextStep = () => {
    const missing = getCurrentStepMissingRequired();
    if (missing.length > 0) {
      setError(`Please fill in all required fields: ${missing.join(', ')}`);
      return;
    }
    // On Supporting documents step, require at least 100 points of ID per applicant before allowing Next
    if (sections && sections[activeStep] && isSupportingDocumentsSection(sections[activeStep].title)) {
      const coCount = repeatableCounts['2b'] ?? 0;
      const applicantRefs: Array<{ ref: string; label: string }> = [
        { ref: 'primary', label: 'Primary applicant' },
        ...Array.from({ length: coCount }, (_, i) => ({ ref: `coApplicant_${i}`, label: `Co-applicant ${i + 1}` })),
      ];
      for (const { ref, label } of applicantRefs) {
        const docs = supportingDocuments.filter((item) => (item.applicantRef || 'primary') === ref);
        if (docs.length === 0) {
          setError(`Supporting documents are required for ${label}. Each applicant must upload documents and provide at least 100 points of ID.`);
          return;
        }
        const points = docs.reduce((sum, item) => sum + (DOCUMENT_POINTS[item.documentType] ?? 0), 0);
        if (points < 100) {
          setError(`${label} has ${points} points of ID. Each applicant must provide at least 100 points of ID (e.g. passport 70 + driver licence 40) before continuing.`);
          return;
        }
      }
    }
    setError(null);
    setActiveStep((s) => s + 1);
  };

  const formContent = useSections ? (
    <>
      <Box sx={{ mb: 2 }}>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          Step {activeStep + 1} of {totalSteps}
        </Typography>
        <LinearProgress
          variant="determinate"
          value={totalSteps > 0 ? ((activeStep + 1) / totalSteps) * 100 : 0}
          sx={{ height: 6, borderRadius: 3, mb: 0.5 }}
        />
        <Typography variant="subtitle2" sx={{ mt: 0.5 }}>{getStepLabel(sections![activeStep])}</Typography>
      </Box>
      <Box sx={{ minHeight: 280 }}>{renderOneSection(sections![activeStep])}</Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 3, gap: 1, flexWrap: 'wrap' }}>
        <Button
          disabled={activeStep === 0}
          onClick={() => { setError(null); setActiveStep((s) => s - 1); }}
          startIcon={<KeyboardArrowLeft />}
          sx={{ minWidth: 100 }}
        >
          Back
        </Button>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {!isLastStep && sections![activeStep].repeatable && (
            <Button
              variant="outlined"
              onClick={() => { setError(null); setActiveStep((s) => s + 1); }}
              sx={{ minWidth: 100 }}
            >
              Skip
            </Button>
          )}
          {isLastStep ? (
            <Button
              type="submit"
              variant="contained"
              disabled={submitting}
              endIcon={submitting ? <CircularProgress size={20} color="inherit" /> : undefined}
              sx={{ minWidth: 140 }}
            >
              {submitting ? 'Submitting…' : 'Submit application'}
            </Button>
          ) : (
            <Button
              variant="contained"
              onClick={handleNextStep}
              endIcon={<KeyboardArrowRight />}
              sx={{ minWidth: 100 }}
            >
              Next
            </Button>
          )}
        </Box>
      </Box>
    </>
  ) : (
    <>
      <Grid container spacing={2}>
        {formFields.map((field) => (
          <Grid item xs={12} key={field.name}>
            {renderField(field)}
          </Grid>
        ))}
      </Grid>
      <Button
        type="submit"
        variant="contained"
        fullWidth
        sx={{ mt: 3 }}
        disabled={submitting}
      >
        {submitting ? 'Submitting…' : 'Submit application'}
      </Button>
    </>
  );

  return (
    <Container maxWidth="sm" sx={{ py: { xs: 2, sm: 4 } }}>
      {formConfig.banner?.bannerImageData && (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', width: '100%', mb: 2 }}>
          <img
            src={`data:${formConfig.banner.bannerImageType || 'image/png'};base64,${formConfig.banner.bannerImageData}`}
            alt="Agency"
            style={{ maxWidth: '100%', maxHeight: 80, objectFit: 'contain' }}
          />
          {formConfig.banner.bannerTitle && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>{formConfig.banner.bannerTitle}</Typography>
          )}
        </Box>
      )}
      <Typography variant="h5" gutterBottom fontWeight="bold">
        {useSections
          ? (formConfig.state === 'VIC' ? 'Victoria Residential Rental Application' : 'NSW Residential Tenancy Application')
          : 'Rental application'}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {summary.address}
        {summary.bedrooms != null && ` • ${summary.bedrooms} bed`}
        {summary.bathrooms != null && ` • ${summary.bathrooms} bath`}
        {summary.dateAvailable && ` • Available from ${formatDisplayDate(summary.dateAvailable)}`}
      </Typography>

      {draftRestored && (
        <Alert severity="info" sx={{ mb: 2 }} onClose={() => setDraftRestored(false)}>
          We&apos;ve restored your saved progress. You can continue below. Note: any documents you had selected were not saved; please add them again before submitting.
        </Alert>
      )}

      <Paper component="form" onSubmit={handleSubmit} sx={{ p: 3 }}>
        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
        {draftSavedAt != null && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            Draft saved locally. You can close this page and continue later.
          </Typography>
        )}
        {formContent}
        <Button
          type="submit"
          variant="contained"
          fullWidth
          sx={{ mt: 3 }}
          disabled={submitting}
        >
          {submitting ? 'Submitting…' : 'Submit application'}
        </Button>
      </Paper>
    </Container>
  );
};

export default RentalApply;
